/**
 * Group info.
 *
 * The single scrolling page: header with icon and name, description,
 * participants with their admin labels, then the destructive actions at
 * the bottom.
 *
 * NOT the three-tab redesign — Members / Media / Settings — that WABetaInfo
 * reports on Android and iOS. That is still in beta, and matching an
 * unreleased layout is how you ship something that looks dated on the day
 * it launches. The shipped single page is what this follows.
 *
 * Every admin action here has a route behind it: rename, add, remove,
 * promote, demote, leave. The server enforces the permissions
 * independently — the UI hides what you cannot do, which is courtesy, not
 * security.
 */

import { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { chats, contacts as contactService, userMessage } from '@zivapp/shared'
import { useAuth } from '../context/index.js'
import Avatar from '../components/ui/Avatar.jsx'
import './GroupInfo.css'

export default function GroupInfo () {
  const { chatId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [chat, setChat] = useState(null)
  const [isLoading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(null)

  const [editingName, setEditingName] = useState(false)
  const [draftName, setDraftName] = useState('')

  const [adding, setAdding] = useState(false)
  const [candidates, setCandidates] = useState([])

  const load = useCallback(async () => {
    try {
      setChat(await chats.get(chatId))
    } catch (err) {
      setError(userMessage(err))
    } finally {
      setLoading(false)
    }
  }, [chatId])

  useEffect(() => { load() }, [load])

  useEffect(() => contactService.subscribe(setCandidates), [])

  if (isLoading) return <p className="gi__state">Loading…</p>
  if (!chat) return <p className="gi__state">{error ?? 'Group not found.'}</p>

  const members = chat.participants ?? []
  const me = members.find(m => m.userId === user?.id)
  const iAmAdmin = me?.role === 'admin'

  const run = async (key, work) => {
    setBusy(key)
    setError(null)
    try {
      await work()
      await load()
    } catch (err) {
      setError(userMessage(err))
    } finally {
      setBusy(null)
    }
  }

  const saveName = () => {
    const name = draftName.trim()
    if (!name || name === chat.name) return setEditingName(false)
    return run('name', async () => {
      await chats.updateGroup(chatId, { name })
      setEditingName(false)
    })
  }

  // Contacts who are not already in the group.
  const addable = candidates.filter(c => {
    const id = c.userId ?? c.user?.id
    return id && !members.some(m => m.userId === id)
  })

  return (
    <div className="gi">
      <header className="gi__bar">
        <button className="gi__back" type="button" onClick={() => navigate(`/chats/${chatId}`)}
          aria-label="Back to the conversation">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h1>Group info</h1>
      </header>

      <div className="gi__scroll">
        <section className="gi__identity">
          <Avatar name={chat.name} url={chat.avatarUrl} size={110} group />

          {editingName ? (
            <div className="gi__rename">
              <input
                className="gi__rename-input"
                value={draftName}
                onChange={e => setDraftName(e.target.value.slice(0, 100))}
                onKeyDown={e => {
                  if (e.key === 'Enter') saveName()
                  if (e.key === 'Escape') setEditingName(false)
                }}
                aria-label="Group subject"
                autoFocus
              />
              <button type="button" onClick={saveName} disabled={busy === 'name'}>
                {busy === 'name' ? 'Saving…' : 'Save'}
              </button>
            </div>
          ) : (
            <h2 className="gi__name">
              {chat.name}
              {iAmAdmin && (
                <button
                  className="gi__edit"
                  type="button"
                  onClick={() => { setDraftName(chat.name ?? ''); setEditingName(true) }}
                  aria-label="Edit group subject"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M4 20h4L19.4 8.6a2.4 2.4 0 0 0-3.4-3.4L4.6 16.6 4 20Z"
                      stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
            </h2>
          )}

          <p className="gi__meta">Group · {members.length} participants</p>
          {chat.description && <p className="gi__description">{chat.description}</p>}
        </section>

        {error && <p className="gi__error" role="alert">{error}</p>}

        <section className="gi__members">
          <div className="gi__section-row">
            <h3>{members.length} participants</h3>
            {iAmAdmin && addable.length > 0 && (
              <button className="gi__add" type="button" onClick={() => setAdding(v => !v)}>
                {adding ? 'Done' : 'Add'}
              </button>
            )}
          </div>

          {adding && (
            <ul className="gi__addable">
              {addable.map(c => {
                const id = c.userId ?? c.user?.id
                const name = c.savedName || c.user?.displayName || 'Unknown'
                return (
                  <li key={id}>
                    <button
                      className="gi__row"
                      type="button"
                      disabled={busy === id}
                      onClick={() => run(id, () => chats.addMembers(chatId, [id]))}
                    >
                      <Avatar name={name} url={c.user?.avatarUrl} size={44} />
                      <span className="gi__row-body">
                        <span className="gi__row-name">{name}</span>
                        <span className="gi__row-sub">
                          {busy === id ? 'Adding…' : 'Tap to add'}
                        </span>
                      </span>
                      <span className="gi__plus" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" />
                        </svg>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          <ul className="gi__list">
            {members.map(member => {
              const isMe = member.userId === user?.id
              return (
                <li key={member.userId}>
                  <div className="gi__row gi__row--member">
                    <Avatar name={member.displayName} url={member.avatarUrl} size={47} />

                    <span className="gi__row-body">
                      <span className="gi__row-name">
                        {isMe ? 'You' : (member.displayName || 'Unknown')}
                      </span>
                      <span className="gi__row-sub">
                        {member.about || 'Hey there! I am on ZIvapp.'}
                      </span>
                    </span>

                    {member.role === 'admin' && (
                      <span className="gi__admin">Group admin</span>
                    )}

                    {iAmAdmin && !isMe && (
                      <span className="gi__actions">
                        <button
                          type="button"
                          disabled={busy === member.userId}
                          onClick={() => run(member.userId, () =>
                            chats.setMemberRole(
                              chatId, member.userId,
                              member.role === 'admin' ? 'member' : 'admin'
                            ))}
                        >
                          {member.role === 'admin' ? 'Dismiss admin' : 'Make admin'}
                        </button>
                        <button
                          type="button"
                          className="gi__danger"
                          disabled={busy === member.userId}
                          onClick={() => run(member.userId, () =>
                            chats.removeMember(chatId, member.userId))}
                        >
                          Remove
                        </button>
                      </span>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </section>

        <section className="gi__exit">
          <button
            className="gi__leave"
            type="button"
            disabled={busy === 'leave'}
            onClick={() => run('leave', async () => {
              await chats.leaveGroup(chatId)
              navigate('/chats', { replace: true })
            })}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M15 17v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v2"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M10 12h11m0 0-3.5-3.5M21 12l-3.5 3.5" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {busy === 'leave' ? 'Leaving…' : 'Exit group'}
          </button>
        </section>
      </div>
    </div>
  )
}
